import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from 'aphrodite';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import { VALIDATION_TYPES } from '~/src/utils/validation';

import Form, { renderFormField } from '~/src/components/Forms';

import Card, { CardTitle } from '~/src/components/Card';
import Button from '~/src/components/Button';
import { useContentFilterOptions } from '~/src/hooks/useContentFilterOptions';

import styles from './styles';

const DefaultLabel = ({ isDefault, onSetAsDefault }) => {
  return isDefault ? (
    <p className={css(styles.settingsOrganizationDefault)}>default</p>
  ) : (
    <a
      role="button"
      className={css(styles.settingsOrganizationSetAsDefault)}
      onClick={onSetAsDefault}
    >
      Set as default
    </a>
  );
};

const SettingsOrganization = ({
  isDefault,
  showSetAsDefault,
  currentOrganizationName,
  currentPrimaryPracticeArea,
  currentSecondaryPracticeArea,
  currentPrimaryState,
  onSave,
  onChange,
  onPrimaryPracticeAreaChange,
  onSecondaryPracticeAreaChange,
  onPrimaryStateChange,
  onSetAsDefault,
  isAdmin,
}) => {
  const { data: filterOptions } = useContentFilterOptions();
  const practiceAreas = filterOptions?.practice_areas || [];
  const jurisdictions = filterOptions?.jurisdictions || [];

  const fields = [
    {
      label: 'Organization name',
      id: 'name',
      type: 'text',
      defaultValue: currentOrganizationName,
      validation: VALIDATION_TYPES.present,
      editable: isAdmin,
    },
  ];

  const [currentFieldValue, setCurrentFieldValue] = useState(
    currentOrganizationName,
  );
  const [primaryPracticeArea, setPrimaryPracticeArea] = useState(
    currentPrimaryPracticeArea || '',
  );
  const [secondaryPracticeArea, setSecondaryPracticeArea] = useState(
    currentSecondaryPracticeArea || '',
  );
  const [primaryState, setPrimaryState] = useState(currentPrimaryState || '');

  useEffect(() => {
    setCurrentFieldValue(currentOrganizationName);
  }, [currentOrganizationName]);

  useEffect(() => {
    setPrimaryPracticeArea(currentPrimaryPracticeArea || '');
  }, [currentPrimaryPracticeArea]);

  useEffect(() => {
    setSecondaryPracticeArea(currentSecondaryPracticeArea || '');
  }, [currentSecondaryPracticeArea]);

  useEffect(() => {
    setPrimaryState(currentPrimaryState || '');
  }, [currentPrimaryState]);

  const handleFormChange = (form) => {
    if (form.fields.name && form.fields.name.value) {
      setCurrentFieldValue(form.fields.name.value);
      onChange(form.fields.name.value);
    }
  };

  const handlePrimaryPracticeAreaChange = (e) => {
    const value = e.target.value;
    setPrimaryPracticeArea(value);
    onPrimaryPracticeAreaChange(value);
  };

  const handleSecondaryPracticeAreaChange = (e) => {
    const value = e.target.value;
    setSecondaryPracticeArea(value);
    onSecondaryPracticeAreaChange(value);
  };

  const handlePrimaryStateChange = (e) => {
    const value = e.target.value;
    setPrimaryState(value);
    onPrimaryStateChange(value);
  };

  const hasChanges =
    currentOrganizationName !== currentFieldValue ||
    primaryPracticeArea !== (currentPrimaryPracticeArea || '') ||
    secondaryPracticeArea !== (currentSecondaryPracticeArea || '') ||
    primaryState !== (currentPrimaryState || '');

  const fieldsProp = fields.map((field) => renderFormField(field));

  const setAsDefaultLabel = showSetAsDefault ? (
    <DefaultLabel isDefault={isDefault} onSetAsDefault={onSetAsDefault} />
  ) : null;

  return (
    <div className={css(styles.settingsCardContainer)}>
      <Card>
        <CardTitle>Organization</CardTitle>
        <div
          className={css(
            styles.settingsRow,
            styles.settingsOrganizationNameRow,
          )}
        >
          {setAsDefaultLabel}
          <Form
            fields={fieldsProp}
            onChange={handleFormChange}
            triggerOnMount={false}
          />
        </div>

        {/* Primary Practice Area */}
        <div className={css(styles.settingsRow)}>
          <FormControl fullWidth size="small">
            <Box pb={1}>
              <FormLabel>
                <Typography variant="body2" fontWeight={500}>
                  Primary practice area
                </Typography>
              </FormLabel>
            </Box>
            <Select
              value={primaryPracticeArea}
              onChange={handlePrimaryPracticeAreaChange}
              disabled={!isAdmin}
              displayEmpty
              inputProps={{ 'aria-label': 'Primary practice area' }}
            >
              <MenuItem value="" disabled>
                Select practice area
              </MenuItem>
              {practiceAreas.map((area) => (
                <MenuItem key={area} value={area}>
                  {area}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Changing your practice area will update the templates available in
              your library.
            </FormHelperText>
          </FormControl>
        </div>

        {/* Secondary Practice Area */}
        <div className={css(styles.settingsRow)}>
          <FormControl fullWidth size="small">
            <Box pb={1}>
              <FormLabel>
                <Typography variant="body2" fontWeight={500}>
                  Secondary practice area (optional)
                </Typography>
              </FormLabel>
            </Box>
            <Select
              value={secondaryPracticeArea}
              onChange={handleSecondaryPracticeAreaChange}
              disabled={!isAdmin}
              displayEmpty
              inputProps={{ 'aria-label': 'Secondary practice area' }}
            >
              <MenuItem value="">None</MenuItem>
              {practiceAreas.map((area) => (
                <MenuItem key={area} value={area}>
                  {area}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select an additional practice area to access more templates.
            </FormHelperText>
          </FormControl>
        </div>

        {/* Jurisdiction */}
        <div className={css(styles.settingsRow)}>
          <FormControl fullWidth size="small">
            <Box pb={1}>
              <FormLabel>
                <Typography variant="body2" fontWeight={500}>
                  Jurisdiction
                </Typography>
              </FormLabel>
            </Box>
            <Select
              value={primaryState}
              onChange={handlePrimaryStateChange}
              disabled={!isAdmin}
              displayEmpty
              inputProps={{ 'aria-label': 'Jurisdiction' }}
            >
              <MenuItem value="" disabled>
                Select jurisdiction
              </MenuItem>
              {jurisdictions.map((jurisdiction) => (
                <MenuItem key={jurisdiction} value={jurisdiction}>
                  {jurisdiction}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Your jurisdiction affects which templates are available.
            </FormHelperText>
          </FormControl>
        </div>
      </Card>
      {isAdmin && (
        <div className={css(styles.settingsAccountButtonContainer)}>
          <Button primary disabled={!hasChanges} onClick={onSave}>
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
};

SettingsOrganization.propTypes = {
  isDefault: PropTypes.bool,
  showSetAsDefault: PropTypes.bool,
  currentOrganizationName: PropTypes.string,
  currentPrimaryPracticeArea: PropTypes.string,
  currentSecondaryPracticeArea: PropTypes.string,
  currentPrimaryState: PropTypes.string,
  onSave: PropTypes.func,
  onChange: PropTypes.func,
  onPrimaryPracticeAreaChange: PropTypes.func,
  onSecondaryPracticeAreaChange: PropTypes.func,
  onPrimaryStateChange: PropTypes.func,
  onSetAsDefault: PropTypes.func,
  isAdmin: PropTypes.bool,
};

SettingsOrganization.defaultProps = {
  onChange: () => {},
  onPrimaryPracticeAreaChange: () => {},
  onSecondaryPracticeAreaChange: () => {},
  onPrimaryStateChange: () => {},
  onSetAsDefault: () => {},
};

export default SettingsOrganization;
